import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from '../../components/ui'
import RedshiftDataSourcePanel from '../revenue-close/RedshiftDataSourcePanel'
import {
  downloadAsc606Export,
  useAsc606AgentReview,
  useAsc606AvailableConnectors,
  useAsc606DataSourceConfig,
  useAsc606Health,
  useAsc606RevenueWaterfallRun,
  useClearAsc606DataSource,
  useSetAsc606DataSource,
  useTestAsc606DataSource,
} from './api'
import type {
  AgentReview,
  Asc606Health,
  Asc606RevenueWaterfallRun,
  Asc606View,
  ExportStatus,
  PerformanceObligation,
  RevenueContract,
  RevenueException,
  WaterfallRow,
} from './types'
import { formatCurrency, severityBadgeClass, statusBadgeClass } from './utils'

const DEFAULT_REDSHIFT_SCHEMA = 'asc_606_revenue_waterfall'
const EXPECTED_REDSHIFT_TABLES = [
  'asc606_runs',
  'asc606_contracts',
  'asc606_performance_obligations',
  'asc606_waterfall',
  'asc606_exceptions',
  'asc606_source_files',
  'asc606_journal_rows',
  'asc606_waterfall_export_rows',
]

export default function Asc606RevenueWaterfallPage({ view = 'overview' }: { view?: Asc606View }) {
  const { data, isLoading, error } = useAsc606RevenueWaterfallRun()
  const { data: health } = useAsc606Health()

  if (view === 'sources') {
    return (
      <SourcesView
        run={data}
        health={health}
        isRunLoading={isLoading}
        hasRunError={Boolean(error)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading ASC 606 revenue waterfall...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-[1180px] space-y-4">
        <DataSourceStrip health={health} />
        <Asc606DataSourceConfiguration health={health} />
        <Panel>
          <PanelBody>
            <p className="text-sm text-rose-600 dark:text-rose-400">
              ASC 606 revenue waterfall data could not be loaded. Check the Close Calendar gate for blocking prerequisites.
            </p>
          </PanelBody>
        </Panel>
      </div>
    )
  }

  if (view === 'review') return <ReviewView run={data} health={health} />
  if (view === 'exports') return <ExportsView run={data} health={health} />
  return <OverviewView run={data} health={health} />
}

function Asc606DataSourceConfiguration({ health }: { health?: Asc606Health }) {
  const { data: config, isLoading: configLoading } = useAsc606DataSourceConfig()
  const { data: connectors = [], isLoading: connectorsLoading } = useAsc606AvailableConnectors()
  const saveMutation = useSetAsc606DataSource()
  const clearMutation = useClearAsc606DataSource()
  const testMutation = useTestAsc606DataSource()

  return (
    <RedshiftDataSourcePanel
      appletName="ASC 606 Revenue Waterfall"
      defaultSchema={DEFAULT_REDSHIFT_SCHEMA}
      expectedTables={EXPECTED_REDSHIFT_TABLES}
      config={config}
      health={health}
      connectors={connectors}
      isLoading={configLoading || connectorsLoading}
      saveMutation={saveMutation}
      clearMutation={clearMutation}
      testMutation={testMutation}
    />
  )
}

function OverviewView({ run, health }: { run: Asc606RevenueWaterfallRun; health?: Asc606Health }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1500px] space-y-6">
      <DataSourceStrip health={health} />
      <DecisionStrip run={run} />

      <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-6 max-xl:grid-cols-1">
        <ContractLedger contracts={run.contracts} />
        <ExceptionRail exceptions={run.exceptions} />
      </div>

      <div className="grid grid-cols-2 gap-6 max-2xl:grid-cols-1">
        <WaterfallTable rows={run.waterfall} />
        <ObligationsTable rows={run.performance_obligations} />
      </div>
    </div>
  )
}

function DataSourceStrip({ health }: { health?: Asc606Health }) {
  const sourceMode = health?.data_source_mode ?? health?.source_mode ?? 'unconfigured'
  let sourceLabel = sourceMode
  if (health?.redshift_connected) {
    sourceLabel = 'Redshift connected'
  } else if (sourceMode === 'unconfigured') {
    sourceLabel = 'Redshift required'
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-dark-border dark:bg-dark-surface sm:px-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={health?.status === 'ok' ? 'success' : 'warning'}>
            Close package {health?.status === 'ok' ? 'available' : 'checking'}
          </Badge>
          <Badge variant={health?.redshift_connected ? 'success' : 'warning'}>
            {sourceLabel}
          </Badge>
          {health?.redshift_schema && (
            <Badge variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
              schema {health.redshift_schema}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Badge variant={health?.langfuse_enabled ? 'success' : 'warning'}>
            Langfuse {health?.langfuse_enabled ? 'enabled' : 'inactive'}
          </Badge>
          <Badge variant="info">ASC 606 policy check</Badge>
        </div>
      </div>
      {health?.message && (
        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
          {health.message}
        </p>
      )}
    </div>
  )
}

function DecisionStrip({ run }: { run: Asc606RevenueWaterfallRun }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.25fr_0.75fr_0.75fr_0.75fr]">
        <div className="border-b border-gray-200 p-4 dark:border-dark-border sm:col-span-2 sm:p-5 xl:col-span-1 xl:border-b-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Posting decision
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="text-2xl font-semibold tracking-normal text-charcoal dark:text-gray-100 sm:text-3xl">
              {formatCurrency(run.summary.recognized_revenue, true)}
            </div>
            <Badge variant="warning">{run.summary.review_required_count} review gates</Badge>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            {run.summary.policy} controls are ready for posting once contract review exceptions are approved.
          </p>
        </div>
        <DecisionMetric label="Billings" value={formatCurrency(run.summary.billings, true)} />
        <DecisionMetric label="Net adjustment" value={formatCurrency(run.summary.net_adjustment, true)} />
        <DecisionMetric label="NetSuite files" value={run.summary.netsuite_ready ? 'Ready' : 'Blocked'} />
      </div>
    </div>
  )
}

function DecisionMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t border-gray-200 p-4 dark:border-dark-border sm:p-5 xl:border-l xl:border-t-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 break-words text-xl font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}

function ContractLedger({ contracts }: { contracts: RevenueContract[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Contract revenue ledger</PanelTitle>
        <PanelDescription>
          Revenue recognition, deferral, and allocation method by contract.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Contract</th>
              <th className="px-4 py-3 text-right font-semibold">Transaction price</th>
              <th className="px-4 py-3 text-right font-semibold">Recognized</th>
              <th className="px-4 py-3 text-right font-semibold">Deferred</th>
              <th className="px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {contracts.map(contract => (
              <tr key={contract.contract_id} className="align-top">
                <td className="px-5 py-4">
                  <div className="font-semibold text-charcoal dark:text-gray-100">
                    {contract.contract_id} - {contract.customer_name}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {contract.contract_type}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {contract.allocation_method}
                  </div>
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(contract.transaction_price)}
                </td>
                <td className="px-4 py-4 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                  {formatCurrency(contract.recognized_current)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(contract.deferred_ending)}
                </td>
                <td className="px-4 py-4">
                  <span className={statusBadgeClass(contract.status)}>
                    {contract.needs_review ? 'Review' : 'Ready'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ExceptionRail({ exceptions }: { exceptions: RevenueException[] }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Controller review queue</PanelTitle>
        <PanelDescription>
          ASC 606 items that require retained evidence or approval.
        </PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-3">
        {exceptions.map(exception => (
          <div key={`${exception.title}-${exception.contract_id}`} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-charcoal dark:text-gray-100">
                  {exception.title}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {exception.contract_id ?? 'Portfolio'} - {exception.owner}
                </div>
              </div>
              <span className={severityBadgeClass(exception.severity)}>
                {exception.severity}
              </span>
            </div>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {exception.detail}
            </p>
          </div>
        ))}
      </PanelBody>
    </Panel>
  )
}

function WaterfallTable({ rows }: { rows: WaterfallRow[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Recognition waterfall</PanelTitle>
        <PanelDescription>
          Opening balance, billings, ASC 606 adjustments, auto-posted recognition, and HITL release.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Activity</th>
              <th className="px-4 py-3 text-right font-semibold">Opening</th>
              <th className="px-4 py-3 text-right font-semibold">Billings</th>
              <th className="px-4 py-3 text-right font-semibold">Recognized</th>
              <th className="px-4 py-3 text-right font-semibold">Ending</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={`${row.event_date}-${row.activity}`}>
                <td className="px-5 py-4">
                  <div className="font-medium text-charcoal dark:text-gray-100">{row.activity}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{row.event_date}</div>
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.opening_deferred_revenue)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.billings)}
                </td>
                <td className="px-4 py-4 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                  {formatCurrency(row.recognized_revenue)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.ending_deferred_revenue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ObligationsTable({ rows }: { rows: PerformanceObligation[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Performance obligations</PanelTitle>
        <PanelDescription>
          Allocation and recognition pattern by ASC 606 obligation.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Obligation</th>
              <th className="px-4 py-3 text-right font-semibold">Allocated</th>
              <th className="px-4 py-3 text-right font-semibold">Recognized</th>
              <th className="px-4 py-3 text-right font-semibold">Remaining</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={row.obligation_id}>
                <td className="px-5 py-4">
                  <div className="font-medium text-charcoal dark:text-gray-100">{row.label}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{row.recognition_pattern}</div>
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.allocated_amount)}
                </td>
                <td className="px-4 py-4 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                  {formatCurrency(row.recognized_current)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.deferred_remaining)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ReviewView({ run, health }: { run: Asc606RevenueWaterfallRun; health?: Asc606Health }) {
  const reviewMutation = useAsc606AgentReview()
  const review = reviewMutation.data

  return (
    <div className="mx-auto w-full min-w-0 max-w-[1300px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <PanelTitle>Agent review</PanelTitle>
              <PanelDescription>
                Deterministic ASC 606 review across contract changes, SSP allocation, and posting readiness.
              </PanelDescription>
            </div>
            <Button
              onClick={() => reviewMutation.mutate({ use_llm: false })}
              disabled={reviewMutation.isPending}
            >
              <ShieldCheckIcon className="mr-2 h-4 w-4" />
              Run Review
            </Button>
          </div>
        </PanelHeader>
        <PanelBody>
          {review ? <AgentReviewResult review={review} /> : (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              Run the review to generate an approval-focused checklist for {run.summary.review_required_count} revenue close items.
            </div>
          )}
        </PanelBody>
      </Panel>
      <ExceptionRail exceptions={run.exceptions} />
    </div>
  )
}

function AgentReviewResult({ review }: { review: AgentReview }) {
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{review.mode}</Badge>
          <Badge variant="info">{review.agent_name}</Badge>
          <Badge variant={review.traced_with_langfuse ? 'success' : 'warning'}>
            Langfuse {review.traced_with_langfuse ? 'traced' : 'inactive'}
          </Badge>
          {review.confidence != null && (
            <Badge variant="secondary">{Math.round(review.confidence * 100)}% confidence</Badge>
          )}
        </div>
        <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
          {review.summary}
        </p>
      </div>
      <div className="grid gap-3">
        {review.findings.map(finding => (
          <div key={`${finding.title}-${finding.contract_id}`} className="rounded-lg border border-gray-200 p-4 dark:border-dark-border">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold text-charcoal dark:text-gray-100">{finding.title}</div>
              <span className={severityBadgeClass(finding.severity)}>{finding.severity}</span>
            </div>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {finding.detail}
            </p>
          </div>
        ))}
      </div>
      <div>
        <div className="text-sm font-semibold text-charcoal dark:text-gray-100">Recommended actions</div>
        <ul className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300">
          {review.recommended_actions.map(action => (
            <li key={action} className="flex gap-2">
              <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
              <span>{action}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function SourcesView({
  run,
  health,
  isRunLoading = false,
  hasRunError = false,
}: {
  run?: Asc606RevenueWaterfallRun
  health?: Asc606Health
  isRunLoading?: boolean
  hasRunError?: boolean
}) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1200px] space-y-6">
      <DataSourceStrip health={health} />
      <Asc606DataSourceConfiguration health={health} />
      <Panel>
        <PanelHeader>
          <PanelTitle>Source evidence</PanelTitle>
          <PanelDescription>
            Close package files and source tables used by the revenue waterfall agent.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="grid gap-3">
          {isRunLoading ? (
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
              Loading ASC 606 source evidence...
            </div>
          ) : hasRunError || !run ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
              ASC 606 source evidence could not be loaded. The Redshift connector can still be reviewed above.
            </div>
          ) : (
            <>
              {run.source_files.map(file => (
                <div key={file.filename} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 dark:border-dark-border">
                  <div>
                    <div className="font-semibold text-charcoal dark:text-gray-100">{file.filename}</div>
                    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{file.description}</div>
                  </div>
                  {file.available ? (
                    <Badge variant="success">Available</Badge>
                  ) : (
                    <Badge variant="danger">Missing</Badge>
                  )}
                </div>
              ))}
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                <div className="text-sm font-semibold text-charcoal dark:text-gray-100">Source tables</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {run.data_source.tables.map(table => (
                    <Badge key={table} variant="secondary">{table}</Badge>
                  ))}
                </div>
              </div>
            </>
          )}
        </PanelBody>
      </Panel>
    </div>
  )
}

function ExportsView({ run, health }: { run: Asc606RevenueWaterfallRun; health?: Asc606Health }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-[1200px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader>
          <PanelTitle>NetSuite exports</PanelTitle>
          <PanelDescription>
            Balanced ASC 606 journal and recognized revenue waterfall files.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="grid gap-4 md:grid-cols-2">
          <ExportCard
            title="Revenue recognition journal"
            status={run.exports.journal_entry}
            onDownload={() => downloadAsc606Export('journal')}
          />
          <ExportCard
            title="Revenue waterfall"
            status={run.exports.waterfall}
            onDownload={() => downloadAsc606Export('waterfall')}
          />
        </PanelBody>
      </Panel>
    </div>
  )
}

function ExportCard({ title, status, onDownload }: { title: string; status: ExportStatus; onDownload: () => void }) {
  return (
    <div className="rounded-lg border border-gray-200 p-5 dark:border-dark-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-charcoal dark:text-gray-100">{title}</div>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{status.filename}</div>
        </div>
        {status.balanced ? (
          <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
        ) : (
          <XCircleIcon className="h-5 w-5 text-rose-600" />
        )}
      </div>
      <div className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <ExportMetric label="Rows" value={String(status.rows)} />
        <ExportMetric label="Debits" value={formatCurrency(status.debit_total, true)} />
        <ExportMetric label="Credits" value={formatCurrency(status.credit_total, true)} />
      </div>
      <Button className="mt-5 w-full" variant="secondary" onClick={onDownload}>
        <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
        Download CSV
      </Button>
    </div>
  )
}

function ExportMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}
